import GetRequest from '../../../main/requests/GetRequest';
import JSONResponseHandler from '../../../main/handlers/JSONResponseHandler';
import { normalizeLanguageCode } from '@/lib/language';
import { resolveCurrentAgencySlug } from '@/lib/agencySlug';
import { LOCATION_FILTER_TYPES } from '@/api/network/locationSuggestions/types/locationSuggestion';
export const B2C_LISTING_FEATURE_KEYS = [
    'ELEVATOR',
    'AIR_CONDITIONING',
    'FIREPLACE',
    'SWIMMING_POOL',
    'ALARM_SYSTEM',
    'INTERNET',
    'BALCONY',
    'TERRACE',
    'GARDEN',
    'PARKING',
    'CELLAR',
];
export const B2C_LISTINGS_SORT_VALUES = [
    'newest',
    'price_asc',
    'price_desc',
    'area_asc',
    'area_desc',
];
export const LOCATION_RADIUS_VALUES = [10, 20, 50];
export const LOCATION_MODE_VALUES = ['exact', 'radius'];
const B2C_LISTING_FEATURE_KEYS_SET = new Set(B2C_LISTING_FEATURE_KEYS);
const B2C_LISTINGS_SORT_VALUES_SET = new Set(B2C_LISTINGS_SORT_VALUES);
const LOCATION_RADIUS_VALUES_SET = new Set(LOCATION_RADIUS_VALUES);
const LOCATION_FILTER_TYPES_SET = new Set(LOCATION_FILTER_TYPES);
const GET_LISTINGS_URL = '/b2c/listings';
function normalizeArrayParam(values) {
    if (!Array.isArray(values) || values.length === 0) {
        return [];
    }
    const uniq = new Set();
    for (const value of values) {
        const normalized = typeof value === 'string' ? value.trim() : '';
        if (!normalized) {
            continue;
        }
        uniq.add(normalized);
    }
    return Array.from(uniq);
}
function normalizeFeatureKeysParam(values) {
    if (!Array.isArray(values) || values.length === 0) {
        return [];
    }
    const uniq = new Set();
    for (const value of values) {
        const normalized = typeof value === 'string' ? value.trim() : '';
        if (!normalized || !B2C_LISTING_FEATURE_KEYS_SET.has(normalized)) {
            continue;
        }
        uniq.add(normalized);
    }
    return Array.from(uniq);
}
function setNumberParam(queryParams, key, value) {
    if (value == null || Number.isNaN(value)) {
        return;
    }
    queryParams.set(key, String(value));
}
function resolveNumberParam(primary, fallback) {
    return primary ?? fallback;
}
function normalizeLocationType(value) {
    if (typeof value !== 'string') {
        return undefined;
    }
    const normalized = value.trim().toUpperCase();
    if (normalized === 'PLACE') {
        return 'POINT';
    }
    if (!normalized || !LOCATION_FILTER_TYPES_SET.has(normalized)) {
        return undefined;
    }
    return normalized;
}
function normalizeRadiusKm(value) {
    if (value == null || Number.isNaN(value)) {
        return undefined;
    }
    const normalized = Math.floor(value);
    if (!LOCATION_RADIUS_VALUES_SET.has(normalized)) {
        return undefined;
    }
    return normalized;
}
function normalizeLocationMode(value) {
    return value === 'radius' ? 'radius' : 'exact';
}
function normalizeSortParam(value) {
    if (typeof value !== 'string') {
        return 'newest';
    }
    const normalized = value.trim();
    if (B2C_LISTINGS_SORT_VALUES_SET.has(normalized)) {
        return normalized;
    }
    return 'newest';
}
export function buildListingsQuery(filters) {
    const queryParams = new URLSearchParams();
    const language = normalizeLanguageCode(filters.language);
    if (language) {
        queryParams.set('language', language);
    }
    if (filters.includeTranslations === false) {
        queryParams.set('includeTranslations', 'false');
    }
    if (filters.dealType) {
        queryParams.set('dealType', filters.dealType);
    }
    const propertyTypes = normalizeArrayParam(filters.propertyTypes ?? (filters.propertyType ? [filters.propertyType] : undefined));
    for (const value of propertyTypes) {
        queryParams.append('propertyTypes', value);
    }
    const features = normalizeFeatureKeysParam(filters.features);
    for (const value of features) {
        queryParams.append('features', value);
    }
    setNumberParam(queryParams, 'priceMin', resolveNumberParam(filters.priceMin, filters.minPrice));
    setNumberParam(queryParams, 'priceMax', resolveNumberParam(filters.priceMax, filters.maxPrice));
    setNumberParam(queryParams, 'areaMin', filters.areaMin);
    setNumberParam(queryParams, 'areaMax', filters.areaMax);
    setNumberParam(queryParams, 'roomsMin', filters.roomsMin);
    setNumberParam(queryParams, 'roomsMax', filters.roomsMax);
    setNumberParam(queryParams, 'bathroomsMin', filters.bathroomsMin);
    setNumberParam(queryParams, 'bathroomsMax', filters.bathroomsMax);
    const locationMode = normalizeLocationMode(filters.locationMode);
    const locationType = normalizeLocationType(filters.locationType);
    const lat = Number.isFinite(filters.lat) ? Number(filters.lat) : undefined;
    const lng = Number.isFinite(filters.lng) ? Number(filters.lng) : undefined;
    const radiusKm = normalizeRadiusKm(filters.radiusKm);
    if (locationMode === 'radius' && lat != null && lng != null && radiusKm != null) {
        queryParams.set('lat', String(lat));
        queryParams.set('lng', String(lng));
        queryParams.set('radiusKm', String(radiusKm));
    }
    else {
        if (filters.municipality?.trim()) {
            queryParams.set('municipality', filters.municipality.trim());
        }
        if (filters.locality?.trim()) {
            queryParams.set('locality', filters.locality.trim());
        }
        if (filters.city?.trim()) {
            queryParams.set('city', filters.city.trim());
        }
        if (filters.district?.trim()) {
            queryParams.set('district', filters.district.trim());
        }
        if (filters.postalCode?.trim()) {
            queryParams.set('postalCode', filters.postalCode.trim());
        }
        if (filters.province?.trim()) {
            queryParams.set('province', filters.province.trim());
        }
        const provinces = normalizeArrayParam(filters.provinces);
        for (const value of provinces) {
            queryParams.append('provinces', value);
        }
        if (filters.provinceCode?.trim()) {
            queryParams.set('provinceCode', filters.provinceCode.trim().toUpperCase());
        }
        if (filters.region?.trim()) {
            queryParams.set('region', filters.region.trim());
        }
        if (filters.countryCode?.trim()) {
            queryParams.set('countryCode', filters.countryCode.trim().toUpperCase());
        }
        if (filters.locationLabel?.trim()) {
            queryParams.set('locationLabel', filters.locationLabel.trim());
        }
        if (locationType) {
            queryParams.set('locationType', locationType);
        }
    }
    const page = Math.max(1, filters.page ?? 1);
    const limit = Math.min(100, Math.max(1, filters.limit ?? 20));
    queryParams.set('page', String(page));
    queryParams.set('limit', String(limit));
    queryParams.set('sort', normalizeSortParam(filters.sort));
    if (filters.agencySlug?.trim()) {
        queryParams.set('agencySlug', filters.agencySlug.trim());
    }
    return queryParams;
}
export class GetListingRequest extends GetRequest {
    constructor(params = {}) {
        super();
        this.responseHandler = new JSONResponseHandler();
        this.params = {
            page: 1,
            limit: 20,
            ...params,
        };
    }
    get url() {
        const agencySlug = this.params.agencySlug ?? resolveCurrentAgencySlug();
        const queryParams = buildListingsQuery({
            ...this.params,
            agencySlug,
        });
        const queryString = queryParams.toString();
        return `${GET_LISTINGS_URL}${queryString ? `?${queryString}` : ''}`;
    }
}
